import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { BRAND, SERVICES } from "@/lib/site";

const SITE_URL =
  (typeof window !== "undefined" && window.location.origin) ||
  "https://flowgridplumbing.com.au";

/**
 * BUSINESS — base LocalBusiness / Plumber schema.
 * Reused everywhere and extended per-page where it makes sense.
 */
function buildBusinessJsonLd({ areaServed, url, name, description } = {}) {
  return {
    "@context": "https://schema.org",
    "@type": "Plumber",
    "@id": `${SITE_URL}#business`,
    name: name || BRAND.name,
    url: url || SITE_URL,
    image: `${SITE_URL}${BRAND.vanUrl.startsWith("http") ? "" : ""}` || BRAND.vanUrl,
    logo: BRAND.logoUrl,
    description:
      description ||
      "Premium plumbing services across Sydney — 24/7 emergency plumbing, hot water systems, gas fitting, jet blasting, leak detection and appliance installation.",
    telephone: "+61415988997",
    email: BRAND.email,
    priceRange: "$$",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Sydney",
      addressRegion: "NSW",
      addressCountry: "AU",
    },
    areaServed: (areaServed || ["Sydney Metro", "Inner West", "Western Sydney", "South-West Sydney"]).map(
      (a) => ({ "@type": "Place", name: a })
    ),
    sameAs: [BRAND.social.instagram, BRAND.social.facebook].filter(Boolean),
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
          "Sunday",
        ],
        opens: "00:00",
        closes: "23:59",
      },
    ],
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Plumbing Services",
      itemListElement: SERVICES.map((s) => ({
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: s.title,
          url: `${SITE_URL}/services/${s.slug}`,
          description: s.short,
        },
      })),
    },
  };
}

export default function SEO({
  title,
  description,
  canonicalPath,
  jsonLd,
  type = "website",
  noindex = false,
}) {
  const { pathname } = useLocation();
  const path = canonicalPath || pathname;
  const canonical = `${SITE_URL}${path === "/" ? "" : path}`;
  const fullTitle = title
    ? `${title} | ${BRAND.name}`
    : `${BRAND.name} | 24/7 Sydney Plumbers — Call ${BRAND.phone}`;
  const desc =
    description ||
    "Premium plumbing services across Sydney — 24/7 emergency response, licensed gas fitters, hot water specialists, jet blasting and leak detection. Call 0415 988 997.";

  const schemas = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
  schemas.unshift(buildBusinessJsonLd());

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content={BRAND.name} />
      <meta property="og:image" content={BRAND.vanUrl} />
      <meta property="og:locale" content="en_AU" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={BRAND.vanUrl} />

      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}

export { buildBusinessJsonLd };
